/**
 * @module services/catalogs/bnfService
 * @description Service for querying the BnF (Bibliothèque nationale de France) catalog via SRU protocol
 *
 * The BnF catalog contains millions of bibliographic records including all works
 * deposited under French legal deposit law (dépôt légal).
 * This service uses the SRU (Search/Retrieve via URL) protocol version 1.2
 * to search and retrieve bibliographic records in Dublin Core format.
 *
 * @see http://catalogue.bnf.fr/api/SRU
 * @see https://api.bnf.fr/fr/api-sru-catalogue-general-bnf
 */

import type { ItemData, ItemType, Creator, CreatorType } from '@types';
import type { BaseCatalogRecord, SRUSearchOptions } from './types';
import { CATALOG_PROXY_PATHS } from './types';
import { DublinCoreBaseService } from './sruBase';
import {
  mapLanguageCode,
  extractPageCount,
  extractYear,
  cleanBnfName,
  cleanBnfPublisher,
  capitalizePublisher,
} from './utils';
import { getCreatorTypesForItemType, getDefaultCreatorType } from '@/data/itemTypes';

// =============================================================================
// TYPES
// =============================================================================

/** BnF search result */
export interface BnfRecord extends BaseCatalogRecord {
  /** ARK identifier (e.g., ark:/12148/cb44496975d) */
  ark: string;
}

/** BnF search response */
export interface BnfSearchResponse {
  totalResults: number;
  records: BnfRecord[];
}

/** Search index options for BnF */
export type BnfSearchIndex =
  | 'bib.anywhere' // All words in record
  | 'bib.author' // Author
  | 'bib.title' // Title
  | 'bib.subject' // Subject
  | 'bib.isbn' // ISBN
  | 'bib.issn' // ISSN
  | 'bib.ean' // EAN
  | 'bib.publisher' // Publisher
  | 'bib.publicationdate' // Publication date
  | 'bib.persistentid'; // ARK identifier

/** Search relation types */
export type BnfSearchRelation =
  | 'all' // All words must be present
  | 'any' // Any word can be present
  | 'adj'; // Exact phrase (adjacent words)

/** Document type codes */
export type BnfDocumentType =
  | 'a' // Printed text
  | 'c' // Notated music
  | 'e' // Cartographic material
  | 'g' // Sound recordings
  | 'h' // Video recordings
  | 'i' // Still images
  | 's'; // Electronic resources

/** Search criteria for multi-criteria search */
export interface BnfSearchCriteria {
  term: string;
  index: BnfSearchIndex;
  relation?: BnfSearchRelation;
}

/** BnF search options */
export interface BnfSearchOptions extends SRUSearchOptions {
  index?: BnfSearchIndex;
  relation?: BnfSearchRelation;
  documentType?: BnfDocumentType;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const BNF_CATALOG_URL = 'https://catalogue.bnf.fr';

// =============================================================================
// BNF SERVICE
// =============================================================================

class BnfService extends DublinCoreBaseService<BnfRecord, BnfSearchOptions> {
  protected readonly serviceName = 'BnF';
  protected readonly proxyPath = CATALOG_PROXY_PATHS.bnf;

  /**
   * Simple search in the BnF catalog
   */
  async search(query: string, options: BnfSearchOptions = {}): Promise<BnfSearchResponse> {
    const {
      index = 'bib.anywhere',
      relation = 'all',
      documentType,
      startRecord = 1,
      maximumRecords = 10,
    } = options;

    // Build CQL query
    let cqlQuery = `${index} ${relation} "${this.escapeCQL(query)}"`;

    if (documentType) {
      cqlQuery += ` and bib.doctype any "${documentType}"`;
    }

    return this.executeSearch(cqlQuery, {
      startRecord,
      maximumRecords,
    }) as Promise<BnfSearchResponse>;
  }

  /**
   * Multi-criteria search in the BnF catalog
   */
  async searchMultiCriteria(
    criteria: BnfSearchCriteria[],
    options: Omit<BnfSearchOptions, 'index' | 'relation'> = {}
  ): Promise<BnfSearchResponse> {
    const { documentType, startRecord = 1, maximumRecords = 10 } = options;

    if (criteria.length === 0) {
      return { totalResults: 0, records: [] };
    }

    const queryParts = criteria.map((c) => {
      const relation = c.relation || 'all';
      return `${c.index} ${relation} "${this.escapeCQL(c.term)}"`;
    });

    let cqlQuery = queryParts.join(' and ');

    if (documentType) {
      cqlQuery += ` and bib.doctype any "${documentType}"`;
    }

    return this.executeSearch(cqlQuery, {
      startRecord,
      maximumRecords,
    }) as Promise<BnfSearchResponse>;
  }

  /**
   * Search by ISBN
   */
  async searchByISBN(isbn: string): Promise<BnfRecord | null> {
    const cleanIsbn = isbn.replace(/[-\s]/g, '');
    const result = await this.search(cleanIsbn, { index: 'bib.isbn', maximumRecords: 1 });
    return result.records[0] || null;
  }

  /**
   * Search by ARK identifier
   */
  async searchByARK(ark: string): Promise<BnfRecord | null> {
    const result = await this.search(ark, { index: 'bib.persistentid', maximumRecords: 1 });
    return result.records[0] || null;
  }

  /**
   * Parse recordData element containing Dublin Core record
   */
  protected parseRecordData(recordDataEl: Element): BnfRecord | null {
    const dcEl = this.findElementByLocalName(recordDataEl, 'dc');
    if (!dcEl) {
      return null;
    }

    // Get ARK from recordIdentifier or from dc:identifier
    let ark = '';
    const recordEl = recordDataEl.parentElement;
    if (recordEl) {
      const recordIdEl = this.findElementByLocalName(recordEl, 'recordIdentifier');
      if (recordIdEl) {
        ark = recordIdEl.textContent || '';
      }
    }

    return this.parseDublinCoreRecord(dcEl, ark);
  }

  /**
   * Parse a Dublin Core record to Zotero format
   */
  private parseDublinCoreRecord(dcEl: Element, ark: string): BnfRecord | null {
    const item: Partial<ItemData> = {
      creators: [],
    };

    // If no ARK from recordIdentifier, try to get from dc:identifier
    if (!ark) {
      const identifiers = this.getDCValues(dcEl, 'identifier');
      for (const id of identifiers) {
        if (id.includes('ark:/12148/')) {
          ark = id;
          break;
        }
      }
    }

    // Get title (remove subtitle after " / ")
    const rawTitle = this.getDCValue(dcEl, 'title');
    if (rawTitle) {
      // BnF often uses "Main title / subtitle or author info" format
      const slashIndex = rawTitle.indexOf(' / ');
      item.title = slashIndex !== -1 ? rawTitle.substring(0, slashIndex).trim() : rawTitle;
    }

    // Determine item type from dc:type
    const types = this.getDCValues(dcEl, 'type');
    item.itemType = this.mapDCType(types);

    // Parse creators
    item.creators = this.parseCreators(dcEl, item.itemType);

    // Get publisher and place
    const publishers = this.getDCValues(dcEl, 'publisher');
    if (publishers.length > 0) {
      const pubInfo = publishers[0];
      // Try "City: Publisher" format first
      if (pubInfo.includes(':')) {
        const parts = pubInfo.split(':');
        item.place = capitalizePublisher(parts[0].trim());
        item.publisher = capitalizePublisher(parts.slice(1).join(':').trim());
      } else {
        // Try "Publisher (City)" format
        const cleaned = cleanBnfPublisher(pubInfo);
        item.publisher = capitalizePublisher(cleaned.publisher);
        if (cleaned.place) {
          item.place = capitalizePublisher(cleaned.place);
        }
      }
    }

    // Get date
    const dates = this.getDCValues(dcEl, 'date');
    if (dates.length > 0) {
      item.date = extractYear(dates[0]);
    }

    // Get identifiers
    this.parseIdentifiers(dcEl, item);

    // Get description/abstract
    const descriptions = this.getDCValues(dcEl, 'description');
    if (descriptions.length > 0) {
      item.abstractNote = descriptions.join('\n\n');
    }

    // Get subjects
    const subjects = this.getDCValues(dcEl, 'subject');
    if (subjects.length > 0) {
      const existingExtra = item.extra || '';
      const subjectsStr = `Subjects: ${subjects.join('; ')}`;
      item.extra = existingExtra ? `${existingExtra}\n${subjectsStr}` : subjectsStr;
    }

    // Get language
    const languages = this.getDCValues(dcEl, 'language');
    if (languages.length > 0) {
      item.language = mapLanguageCode(languages[0]);
    }

    // Get format (may contain page info)
    const formats = this.getDCValues(dcEl, 'format');
    for (const format of formats) {
      const pageCount = extractPageCount(format);
      if (pageCount) {
        item.numPages = pageCount;
        break;
      }
    }

    // Validate
    this.validateItem(item);

    // Build catalog URL
    let catalogUrl: string | undefined;
    if (ark) {
      const arkMatch = ark.match(/ark:\/12148\/\w+/);
      const arkId = arkMatch ? arkMatch[0] : ark;
      catalogUrl = `${BNF_CATALOG_URL}/${arkId}`;
    }

    return {
      id: ark || `bnf-${Date.now()}`,
      ark: ark || `bnf-${Date.now()}`,
      item,
      catalogUrl,
    };
  }

  /**
   * Parse creators from Dublin Core
   */
  private parseCreators(dcEl: Element, itemType: ItemType): Creator[] {
    const creators: Creator[] = [];

    // Get dc:creator elements (main authors)
    const creatorValues = this.getDCValues(dcEl, 'creator');
    for (const value of creatorValues) {
      const creator = this.parseCreatorName(value, 'author', itemType);
      if (creator) {
        creators.push(creator);
      }
    }

    // Get dc:contributor elements (other contributors)
    const contributorValues = this.getDCValues(dcEl, 'contributor');
    for (const value of contributorValues) {
      const creator = this.parseCreatorName(value, 'contributor', itemType);
      if (creator) {
        creators.push(creator);
      }
    }

    return creators;
  }

  /**
   * Parse a creator name with role detection and cleaning
   * Handles BnF formats like:
   * - "Bruno (1946-....). Auteur du texte"
   * - "Michel (1814-1876). Auteur du texte"
   * - "Arthur (1899-2000). Éditeur scientifique"
   */
  private parseCreatorName(
    name: string,
    defaultType: CreatorType,
    itemType: ItemType
  ): Creator | null {
    if (!name) return null;

    // Clean name and extract role using shared utility
    const { cleanName: cleaned, role: detectedRole } = cleanBnfName(name);

    let creatorType: CreatorType = (detectedRole as CreatorType) || defaultType;

    // Also check for parenthetical role abbreviations (e.g., "(dir.)", "(trad.)")
    let cleanName = cleaned;
    const roleMatch = cleanName.match(/\(([^)]+)\)\s*$/);
    if (roleMatch) {
      const role = roleMatch[1].toLowerCase();
      cleanName = cleanName.replace(roleMatch[0], '').trim();

      if (role.includes('dir') || role.includes('direction')) {
        creatorType = 'editor';
      } else if (role.includes('trad') || role.includes('translation')) {
        creatorType = 'translator';
      } else if (role.includes('éd') || role.includes('edit')) {
        creatorType = 'editor';
      } else if (role.includes('préf') || role.includes('pref')) {
        creatorType = 'contributor';
      }
    }

    // Validate creator type for item type
    const validTypes = getCreatorTypesForItemType(itemType);
    if (!validTypes.includes(creatorType)) {
      creatorType = getDefaultCreatorType(itemType);
    }

    // Parse name format: "LastName, FirstName" or single name
    if (cleanName.includes(',')) {
      const [lastName, firstName] = cleanName.split(',', 2);
      return {
        creatorType,
        lastName: lastName.trim(),
        firstName: firstName?.trim() || undefined,
      };
    } else {
      return {
        creatorType,
        name: cleanName,
      };
    }
  }

  /**
   * Parse identifiers from Dublin Core
   */
  private parseIdentifiers(dcEl: Element, item: Partial<ItemData>): void {
    const identifiers = this.getDCValues(dcEl, 'identifier');

    for (const id of identifiers) {
      const cleanId = id.trim();

      // ISBN patterns
      if (
        /^(ISBN|978|979|2-|2 )/.test(cleanId) ||
        /^\d{10,13}$/.test(cleanId.replace(/[-\s]/g, ''))
      ) {
        const isbnMatch = cleanId.match(/[\dX]{10,13}/i);
        if (isbnMatch && !item.ISBN) {
          item.ISBN = isbnMatch[0];
        }
      }

      // ISSN pattern
      if (/^ISSN/.test(cleanId) || /^\d{4}-\d{3}[\dX]$/i.test(cleanId)) {
        const issnMatch = cleanId.match(/\d{4}-\d{3}[\dX]/i);
        if (issnMatch && !item.ISSN) {
          item.ISSN = issnMatch[0];
        }
      }

      // URL
      if (cleanId.startsWith('http') && !item.url) {
        item.url = cleanId;
      }
    }
  }

  /**
   * Map Dublin Core type to Zotero item type
   */
  private mapDCType(types: string[]): ItemType {
    const typeStr = types.join(' ').toLowerCase();

    if (typeStr.includes('thèse') || typeStr.includes('thesis')) {
      return 'thesis';
    }
    if (
      typeStr.includes('périodique') ||
      typeStr.includes('periodical') ||
      typeStr.includes('serial')
    ) {
      return 'journalArticle';
    }
    if (typeStr.includes('article')) {
      return 'journalArticle';
    }
    if (typeStr.includes('carte') || typeStr.includes('map')) {
      return 'map';
    }
    if (typeStr.includes('image') || typeStr.includes('photo') || typeStr.includes('estampe')) {
      return 'artwork';
    }
    if (typeStr.includes('vidéo') || typeStr.includes('video') || typeStr.includes('film')) {
      return 'videoRecording';
    }
    if (typeStr.includes('son') || typeStr.includes('audio') || typeStr.includes('musique')) {
      return 'audioRecording';
    }
    if (typeStr.includes('manuscrit') || typeStr.includes('manuscript')) {
      return 'manuscript';
    }
    if (typeStr.includes('partition') || typeStr.includes('score')) {
      return 'audioRecording';
    }
    if (typeStr.includes('logiciel') || typeStr.includes('software')) {
      return 'computerProgram';
    }
    if (typeStr.includes('site') || typeStr.includes('web')) {
      return 'webpage';
    }

    return 'book';
  }
}

// =============================================================================
// EXPORTS
// =============================================================================

export const bnfService = new BnfService();
export default bnfService;
